import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import NumberField from "../../../Components/NumberField";
import Handler from "../../../Helpers/Handler";
import { firstError, invalidClass, numberError, RULES, textError } from "../../../Helpers/bookingRules";
import { StatusPill } from "../../../Components/Needed";
import { BsCalendarRange, BsChevronLeft, BsChevronRight } from "react-icons/bs";

// Peak/off-peak day management for PricingUtils.peakPercentageFor, which
// already reads this collection on every quote (Customer app + Admin
// Registration alike). Until this page existed nothing could ever write to
// it. Custom month grid by design - no calendar library dependency, per the
// build decision recorded in claude/ADMIN_BOOKING_LIFECYCLE_PORT_PLAN.md.
//
// Multi-date selection: a plain click toggles a single day in/out of the
// selection (so scattered days - "every Saturday" - can be picked one at a
// time), while a shift-click or a click-and-drag selects a contiguous range
// in one go, replacing whatever was selected before it. Selecting more than
// one day switches the side panel into "bulk" mode, which applies one
// name/description/percentages set to every selected day via the bulk
// endpoint instead of the single-day upsert.

const SEGMENTS = [
    { key: "hatchback", label: "Hatchback", subs: [{ key: "small", label: "Small" }, { key: "compact", label: "Compact" }, { key: "premium", label: "Premium" }, { key: "luxury", label: "Luxury" }] },
    { key: "sedan", label: "Sedan", subs: [{ key: "compact", label: "Compact" }, { key: "premium", label: "Premium" }, { key: "luxury", label: "Luxury" }] },
    { key: "suv", label: "SUV", subs: [{ key: "compact", label: "Compact" }, { key: "mpv", label: "MPV" }, { key: "xuv", label: "XUV" }, { key: "luxury", label: "Luxury" }] },
];

const emptyPercentages = () => ({
    hatchback: { small: 0, compact: 0, premium: 0, luxury: 0 },
    sedan: { compact: 0, premium: 0, luxury: 0 },
    suv: { compact: 0, mpv: 0, xuv: 0, luxury: 0 },
});

const averagePercentage = (percentages) => {
    const values = [];
    SEGMENTS.forEach((seg) => seg.subs.forEach((sub) => values.push(Number(percentages?.[seg.key]?.[sub.key]) || 0)));
    return values.length ? values.reduce((a, b) => a + b, 0) / values.length : 0;
};

// none: no entry at all. disabled: entry exists but status is off. Otherwise
// bucketed by the average of all 11 percentages, same semantics the legacy
// calendar used (green/amber/red), plus "markdown" for a below-zero average
// since the field allows a discount day, not only a surge.
const dayBucket = (entry) => {
    if (!entry) return "none";
    if (entry.status === false) return "disabled";
    const avg = averagePercentage(entry.percentages);
    if (avg >= 100) return "surge";
    if (avg > 0) return "some";
    if (avg < 0) return "markdown";
    return "zero";
};

const BUCKET_STYLE = {
    none: { bg: "bg-light", text: "text-muted", label: "No entry" },
    zero: { bg: "bg-success-subtle", text: "text-success-emphasis", label: "0% avg" },
    some: { bg: "bg-warning-subtle", text: "text-warning-emphasis", label: "Above 0%" },
    surge: { bg: "bg-danger-subtle", text: "text-danger-emphasis", label: "100%+ avg" },
    markdown: { bg: "bg-info-subtle", text: "text-info-emphasis", label: "Below 0% (discount)" },
    disabled: { bg: "bg-secondary-subtle", text: "text-muted", label: "Disabled" },
};

const toIso = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
};

const isoToDate = (iso) => {
    const [y, m, d] = iso.split("-").map(Number);
    return new Date(y, m - 1, d);
};

// Inclusive day range between two iso dates, order-independent. Both ends
// always come from cells rendered in the currently visible month, so this
// never has to worry about spanning a month whose entries aren't loaded.
const dayRange = (isoA, isoB) => {
    let a = isoToDate(isoA);
    let b = isoToDate(isoB);
    if (a > b) { const t = a; a = b; b = t; }
    const out = [];
    const cur = new Date(a);
    while (cur <= b) {
        out.push(toIso(cur));
        cur.setDate(cur.getDate() + 1);
    }
    return out;
};

const buildMonthGrid = (year, month) => {
    const total = new Date(year, month + 1, 0).getDate();
    const lead = new Date(year, month, 1).getDay();
    const cells = [];
    for (let i = 0; i < lead; i++) cells.push(null);
    for (let d = 1; d <= total; d++) cells.push(new Date(year, month, d));
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
};

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const formatLongDate = (iso) => {
    if (!iso) return "";
    const [y, m, d] = iso.split("-").map(Number);
    return new Date(y, m - 1, d).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });
};

const formatShortDate = (iso) => {
    if (!iso) return "";
    const [y, m, d] = iso.split("-").map(Number);
    return new Date(y, m - 1, d).toLocaleDateString("en-IN", { day: "numeric", month: "short" });
};

const PricingCalendarPage = () => {
    const today = new Date();
    const [cursor, setCursor] = useState({ year: today.getFullYear(), month: today.getMonth() });
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedDates, setSelectedDates] = useState(() => new Set());
    const [anchorDate, setAnchorDate] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const [dragAnchor, setDragAnchor] = useState(null);
    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState({});
    // A new day or selection starts with a clean form.
    useEffect(() => { setErrors({}); }, [form?.iso, form?.mode]);

    const entryByDate = useMemo(() => {
        const map = {};
        entries.forEach((e) => { if (e?.calendarDate) map[toIso(new Date(e.calendarDate))] = e; });
        return map;
    }, [entries]);

    const load = useCallback(async () => {
        setLoading(true);
        const start = new Date(cursor.year, cursor.month, 1);
        const end = new Date(cursor.year, cursor.month + 1, 0, 23, 59, 59, 999);
        const response = await Handler({ method: "post", url: "/admin/booking/pricing-calendar/view", data: { startDate: start.toISOString(), endDate: end.toISOString() } });
        setEntries(response?.success ? (response?.data?.data || []) : []);
        setLoading(false);
    }, [cursor]);

    useEffect(() => { load(); }, [load]);

    // Release the drag even if the mouse comes up somewhere off the grid
    // (a gap cell, the edit panel, outside the window entirely).
    useEffect(() => {
        const onWindowMouseUp = () => { setIsDragging(false); setDragAnchor(null); };
        window.addEventListener("mouseup", onWindowMouseUp);
        return () => window.removeEventListener("mouseup", onWindowMouseUp);
    }, []);

    const clearSelection = () => {
        setSelectedDates(new Set());
        setAnchorDate(null);
        setForm(null);
    };

    const changeMonth = (delta) => {
        setCursor((prev) => {
            const d = new Date(prev.year, prev.month + delta, 1);
            return { year: d.getFullYear(), month: d.getMonth() };
        });
        clearSelection();
    };

    const goToday = () => {
        setCursor({ year: today.getFullYear(), month: today.getMonth() });
        clearSelection();
    };

    // Builds the side-panel form for whatever is currently selected: null for
    // nothing, a single-day form prefilled from its existing entry (or blank
    // for a new day) for exactly one date, and a blank "apply to all" form
    // for more than one.
    const buildFormForSelection = useCallback((set) => {
        const arr = Array.from(set);
        if (arr.length === 0) return null;
        if (arr.length === 1) {
            const iso = arr[0];
            const existing = entryByDate[iso];
            return {
                mode: "single",
                iso,
                name: existing?.name || "",
                description: existing?.description || "",
                percentages: existing?.percentages ? JSON.parse(JSON.stringify(existing.percentages)) : emptyPercentages(),
                existing: existing || null,
            };
        }
        return {
            mode: "bulk",
            name: "",
            description: "",
            percentages: emptyPercentages(),
        };
    }, [entryByDate]);

    // Plain click: toggle this single day in the existing selection without
    // touching the rest of it. Only resyncs the form when the selection's
    // mode actually changes (empty/single/bulk) - staying inside bulk mode
    // (still >1 day after the toggle) leaves whatever the admin has already
    // typed alone.
    const selectSingle = (iso) => {
        setSelectedDates((prev) => {
            const next = new Set(prev);
            if (next.has(iso)) next.delete(iso); else next.add(iso);
            const cameFromMulti = prev.size > 1;
            const goingToMulti = next.size > 1;
            if (!(cameFromMulti && goingToMulti)) setForm(buildFormForSelection(next));
            return next;
        });
        setAnchorDate(iso);
    };

    // Shift-click or a finished drag: always a fresh contiguous range,
    // replacing whatever was selected before - a new range is a new bulk
    // edit intent, so any unsaved bulk text is discarded along with it.
    const selectRange = (fromIso, toIsoValue) => {
        const rangeSet = new Set(dayRange(fromIso, toIsoValue));
        setSelectedDates(rangeSet);
        setForm(buildFormForSelection(rangeSet));
        setAnchorDate(toIsoValue);
    };

    const onDayMouseDown = (iso, e) => {
        e.preventDefault();
        if (e.shiftKey && anchorDate) {
            selectRange(anchorDate, iso);
            return;
        }
        setIsDragging(true);
        setDragAnchor(iso);
    };

    const onDayMouseEnter = (iso) => {
        if (isDragging && dragAnchor) selectRange(dragAnchor, iso);
    };

    const onDayMouseUp = (iso) => {
        if (!isDragging) return;
        const moved = dragAnchor !== null && dragAnchor !== iso;
        setIsDragging(false);
        setDragAnchor(null);
        if (!moved) selectSingle(iso);
        // if it moved, onDayMouseEnter already committed the final range
    };

    const closePanel = () => clearSelection();

    const updatePct = (segment, sub, value) => {
        setForm((prev) => ({ ...prev, percentages: { ...prev.percentages, [segment]: { ...prev.percentages[segment], [sub]: value } } }));
        setErrors((prev) => ({ ...prev, pct: { ...(prev.pct || {}), [`${segment}.${sub}`]: null } }));
    };

    const save = async () => {
        if (!form) return;
        // Same limits as the API (BookingRules.js).
        const percentages = {};
        const pct = {};
        SEGMENTS.forEach((seg) => {
            percentages[seg.key] = {};
            seg.subs.forEach((sub) => {
                const raw = form.percentages?.[seg.key]?.[sub.key];
                const msg = numberError(raw, { label: `${seg.label} ${sub.label}`, required: true, min: RULES.CAL_PCT_MIN, max: RULES.CAL_PCT_MAX, maxDecimals: 2 });
                if (msg) pct[`${seg.key}.${sub.key}`] = msg;
                percentages[seg.key][sub.key] = Number(raw);
            });
        });
        const next = {
            name: textError(form.name, { label: "Name", required: true, min: RULES.CAL_NAME_MIN, max: RULES.CAL_NAME_MAX }),
            description: textError(form.description, { label: "Description", required: true, min: RULES.CAL_DESC_MIN, max: RULES.CAL_DESC_MAX }),
            days: form.mode === "bulk" && selectedDates.size > RULES.CAL_BULK_MAX ? `Select at most ${RULES.CAL_BULK_MAX} days at once` : null,
            pct,
        };
        setErrors(next);
        const problem = firstError(next);
        if (problem) { toast.error(problem); return; }

        setSaving(true);
        if (form.mode === "bulk") {
            const dates = Array.from(selectedDates);
            const response = await Handler({
                method: "patch",
                url: "/admin/booking/pricing-calendar/bulk",
                data: { dates, name: form.name.trim(), description: form.description.trim(), percentages },
            });
            if (response?.success) {
                toast.success(response?.data?.message || "Saved");
                await load();
                closePanel();
            } else {
                toast.error(response?.data?.message || "Could not save these days");
            }
        } else {
            const response = await Handler({
                method: "patch",
                url: "/admin/booking/pricing-calendar",
                data: { calendarDate: form.iso, name: form.name.trim(), description: form.description.trim(), percentages },
            });
            if (response?.success) {
                toast.success(response?.data?.message || "Saved");
                await load();
                closePanel();
            } else {
                toast.error(response?.data?.message || "Could not save this day");
            }
        }
        setSaving(false);
    };

    const toggleStatus = async () => {
        if (!form?.existing) return;
        const nextStatus = form.existing.status === false;
        setSaving(true);
        const response = await Handler({
            method: "patch",
            url: `/admin/booking/pricing-calendar/${form.existing.pricingCalendarId}/status`,
            params: { status: nextStatus },
        });
        if (response?.success) {
            toast.success(response?.data?.message || "Updated");
            await load();
            closePanel();
        } else {
            toast.error(response?.data?.message || "Could not update status");
        }
        setSaving(false);
    };

    const grid = useMemo(() => buildMonthGrid(cursor.year, cursor.month), [cursor]);
    const monthLabel = new Date(cursor.year, cursor.month, 1).toLocaleString("en-IN", { month: "long", year: "numeric" });
    const isToday = (d) => d && toIso(d) === toIso(today);

    const selectionSummary = useMemo(() => {
        const arr = Array.from(selectedDates).sort();
        if (arr.length <= 4) return arr.map(formatShortDate).join(", ");
        return `${formatShortDate(arr[0])} → ${formatShortDate(arr[arr.length - 1])}`;
    }, [selectedDates]);

    const isBulk = form?.mode === "bulk";

    return (
        <div className="container-fluid py-4">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-1">
                <div>
                    <h5 className="fw-semibold mb-1 d-flex align-items-center gap-2"><BsCalendarRange className="text-primary" />Pricing Calendar</h5>
                    <div className="text-muted small">Peak-day surcharges (or discounts) applied on top of the normal rental rate. Every quote already reads this &mdash; mark a day here for it to actually apply.</div>
                </div>
            </div>

            <div className="row g-3 mt-1">
                {/* ---- Month grid ---- */}
                <div className="col-lg-8">
                    <div className="card border-0 shadow-sm rounded-4 p-3">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <button className="btn btn-sm btn-outline-secondary" onClick={() => changeMonth(-1)}><BsChevronLeft /></button>
                            <div className="d-flex align-items-center gap-2">
                                <h6 className="fw-semibold mb-0">{monthLabel}</h6>
                                <button className="btn btn-sm btn-link p-0" onClick={goToday}>Today</button>
                            </div>
                            <button className="btn btn-sm btn-outline-secondary" onClick={() => changeMonth(1)}><BsChevronRight /></button>
                        </div>

                        <div className="d-grid mb-1" style={{ gridTemplateColumns: "repeat(7, 1fr)", gap: 6 }}>
                            {WEEKDAYS.map((w) => (
                                <div key={w} className="text-center text-muted small fw-semibold">{w}</div>
                            ))}
                        </div>

                        <div className="d-grid" style={{ gridTemplateColumns: "repeat(7, 1fr)", gap: 6, userSelect: isDragging ? "none" : undefined }}>
                            {grid.map((d, i) => {
                                if (!d) return <div key={i} />;
                                const iso = toIso(d);
                                const entry = entryByDate[iso];
                                const bucket = dayBucket(entry);
                                const style = BUCKET_STYLE[bucket];
                                const avg = entry ? Math.round(averagePercentage(entry.percentages)) : null;
                                const isSelected = selectedDates.has(iso);
                                return (
                                    <button
                                        type="button"
                                        key={iso}
                                        onMouseDown={(e) => onDayMouseDown(iso, e)}
                                        onMouseEnter={() => onDayMouseEnter(iso)}
                                        onMouseUp={() => onDayMouseUp(iso)}
                                        title={entry?.name || ""}
                                        className={`btn p-1 rounded-3 ${style.bg} ${isSelected ? "border border-primary border-2" : "border"} ${isToday(d) ? "fw-bold" : ""}`}
                                        style={{ aspectRatio: "1", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}
                                    >
                                        <span className={`small ${style.text}`}>{d.getDate()}</span>
                                        {entry && (
                                            <span className={`${style.text}`} style={{ fontSize: ".65rem" }}>
                                                {bucket === "disabled" ? "off" : `${avg > 0 ? "+" : ""}${avg}%`}
                                            </span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>

                        {loading && <div className="text-muted small mt-3">Loading...</div>}

                        <div className="text-muted small mt-3">
                            Click a day to select it, click again to deselect. Shift-click or drag across days to select a range &mdash; click extra days afterwards to add them to it.
                        </div>

                        <div className="d-flex flex-wrap gap-3 mt-3">
                            {Object.entries(BUCKET_STYLE).map(([key, s]) => (
                                <div key={key} className="d-flex align-items-center gap-1 small text-muted">
                                    <span className={`rounded-circle ${s.bg} border`} style={{ width: 10, height: 10, display: "inline-block" }} />
                                    {s.label}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* ---- Edit panel ---- */}
                <div className="col-lg-4">
                    <div className="card border-0 shadow-sm rounded-4 p-3" style={{ position: "sticky", top: 16 }}>
                        {!selectedDates.size || !form ? (
                            <div className="text-muted small py-4 text-center">Select a day on the calendar to view or create a peak-pricing entry.</div>
                        ) : (
                            <>
                                <div className="d-flex justify-content-between align-items-start mb-2">
                                    <div>
                                        {isBulk ? (
                                            <>
                                                <h6 className="fw-semibold mb-0">{selectedDates.size} days selected</h6>
                                                <div className="text-muted small">{selectionSummary}</div>
                                            </>
                                        ) : (
                                            <>
                                                <h6 className="fw-semibold mb-0">{formatLongDate(form.iso)}</h6>
                                                <div className="text-muted small">avg {Math.round(averagePercentage(form.percentages))}%</div>
                                            </>
                                        )}
                                    </div>
                                    {!isBulk && form.existing && <StatusPill active={form.existing.status !== false} activeText="Active" inactiveText="Disabled" />}
                                </div>

                                {isBulk && (
                                    <div className="alert alert-warning py-2 px-3 small mb-3">
                                        This overwrites the name, description and every percentage on all {selectedDates.size} selected days.
                                    </div>
                                )}

                                <div className="mb-2">
                                    <label className="form-label small text-muted mb-1">Name</label>
                                    <input type="text" maxLength={RULES.CAL_NAME_MAX} className={`form-control${invalidClass(errors.name)}`} placeholder="e.g. Diwali surcharge" value={form.name} onChange={(e) => { setForm((prev) => ({ ...prev, name: e.target.value })); setErrors((p) => ({ ...p, name: null })); }} aria-invalid={!!errors.name} />
                                    {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                                </div>
                                <div className="mb-3">
                                    <label className="form-label small text-muted mb-1">Description</label>
                                    <textarea maxLength={RULES.CAL_DESC_MAX} className={`form-control${invalidClass(errors.description)}`} rows={2} value={form.description} onChange={(e) => { setForm((prev) => ({ ...prev, description: e.target.value })); setErrors((p) => ({ ...p, description: null })); }} aria-invalid={!!errors.description} />
                                    {errors.description && <div className="invalid-feedback">{errors.description}</div>}
                                    {errors.days && <div className="text-danger small mt-1" role="alert">{errors.days}</div>}
                                </div>

                                {SEGMENTS.map((seg) => (
                                    <div key={seg.key} className="mb-2">
                                        <div className="text-uppercase text-muted small fw-semibold mb-1" style={{ letterSpacing: ".04em" }}>{seg.label}</div>
                                        <div className="row g-2">
                                            {seg.subs.map((sub) => (
                                                <div className="col-6" key={sub.key}>
                                                    <label className="form-label small text-muted mb-1">{sub.label}</label>
                                                    <div className="input-group input-group-sm">
                                                        <NumberField
                                                            hint="bubble"
                                                            className={`form-control${invalidClass(errors.pct?.[`${seg.key}.${sub.key}`])}`}
                                                            aria-label={`${seg.label} ${sub.label} percentage`}
                                                            aria-invalid={!!errors.pct?.[`${seg.key}.${sub.key}`]}
                                                            min={RULES.CAL_PCT_MIN}
                                                            max={RULES.CAL_PCT_MAX}
                                                            value={form.percentages?.[seg.key]?.[sub.key] ?? 0}
                                                            onChange={(v) => updatePct(seg.key, sub.key, v)}
                                                        />
                                                        <span className="input-group-text">%</span>
                                                    </div>
                                                    {errors.pct?.[`${seg.key}.${sub.key}`] && <div className="text-danger small">{errors.pct[`${seg.key}.${sub.key}`]}</div>}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                                <div className="text-muted small mb-3">&minus;50 to 300. Negative marks the day down instead of up.</div>

                                <div className="d-flex gap-2">
                                    <button className="btn btn-primary flex-grow-1" onClick={save} disabled={saving}>
                                        {saving ? "Saving..." : isBulk ? `Apply to ${selectedDates.size} days` : form.existing ? "Save changes" : "Create entry"}
                                    </button>
                                    <button className="btn btn-outline-secondary" onClick={closePanel} disabled={saving}>Cancel</button>
                                </div>
                                {!isBulk && form.existing && (
                                    <button
                                        className={`btn btn-sm w-100 mt-2 ${form.existing.status === false ? "btn-outline-success" : "btn-outline-danger"}`}
                                        onClick={toggleStatus}
                                        disabled={saving}
                                    >
                                        {form.existing.status === false ? "Enable this day" : "Disable this day"}
                                    </button>
                                )}
                                {!isBulk && form.existing?.pricingCalendarId && (
                                    <div className="text-muted small mt-2">{form.existing.pricingCalendarId}</div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PricingCalendarPage;
